import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart"
import { PieChart, Pie, BarChart, Bar, XAxis, YAxis, Cell } from "recharts"
import { COLORS } from "./constants"
import type { ChartConfigEntry, RaceCount } from "./types"

interface EditionChartsProps {
  editionStats: RaceCount[]
  editionChartConfig: Record<string, ChartConfigEntry>
}

export const EditionCharts = ({ editionStats, editionChartConfig }: EditionChartsProps) => {
  if (!editionStats.length) return null

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {/* Edition Pie Chart */}
      <Card className="dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="text-base sm:text-lg">Distribución por Edición</CardTitle>
        </CardHeader>
        <CardContent className="p-2 sm:p-6">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            {/* Legend as a list on the left */}
            <div className="w-full lg:w-1/3 order-2 lg:order-1">
              <div className="space-y-2">
                {editionStats.map((edition, index) => (
                  <div
                    key={edition.name}
                    className="flex items-center justify-between text-xs sm:text-sm"
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <div
                        className="w-3 h-3 rounded flex-shrink-0"
                        style={{
                          backgroundColor: COLORS[index % COLORS.length],
                        }}
                      />
                      <span className="truncate">{edition.name}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      <span className="text-muted-foreground">{edition.count}</span>
                      <span className="font-medium">({edition.percentage}%)</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pie Chart on the right */}
            <div className="w-full lg:w-2/3 order-1 lg:order-2">
              <ChartContainer config={editionChartConfig} className="w-full">
                <PieChart>
                  <Pie
                    data={editionStats}
                    innerRadius={40}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="name"
                  >
                    {editionStats.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        formatter={(value, name) => [
                          `${name}: `,
                          `${value} jugadores (${
                            editionStats.find((e) => e.name === name)?.percentage
                          }%)`,
                        ]}
                      />
                    }
                  />
                </PieChart>
              </ChartContainer>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edition Bar Chart */}
      <Card className="dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="text-base sm:text-lg">Participación por Edición</CardTitle>
        </CardHeader>
        <CardContent className="p-2">
          <ChartContainer config={editionChartConfig} className=" w-full">
            <BarChart
              data={editionStats}
              margin={{
                top: 0,
                right: 0,
                left: -30,
                bottom: 0,
              }}
              barSize={80}
              barGap={5}
              barCategoryGap={1}
            >
              <XAxis
                dataKey="name"
                angle={0}
                textAnchor="end"
                height={30}
                interval={0}
                fontSize={10}
                tick={{ fontSize: 10, dx: 15 }}
              />
              <YAxis fontSize={10} />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    formatter={(value) => [`${value} jugadores`]}
                    labelFormatter={(label) => `Edición: ${label}`}
                  />
                }
              />
              <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]}>
                {editionStats.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  )
}
